/**
 * Lane grouping — the pure half of the v2 board (CRM UI streamline D-8).
 *
 * The classic board answered "which conversations exist" and made the
 * recruiter sort out the rest with six status chips. The v2 board answers the
 * only question a supervisor actually opens it with: WHAT NEEDS ME, and in
 * what order. That triage is this file, and it is deliberately pure so it can
 * be proven rather than eyeballed — the rendering half (BoardLanes) never
 * decides which lane a row belongs to.
 *
 * Two properties the tests hold this to, because a triage list that quietly
 * drops or double-counts a lead is worse than no triage at all:
 *  - DISJOINT: a conversation appears in exactly one lane (first match wins,
 *    in the order below — a row that is both awaiting_review and mid-draft is
 *    triage debt, not "active now").
 *  - TOTAL: every distinct id handed in comes back out in some lane.
 *    `scheduled_idle` is the catch-all that makes the second property true by
 *    construction; no row can fall between the predicates.
 *
 * Closed rows are their own lane and can never leak into an open one — the
 * `closed` argument is authoritative for its ids, and a row that arrives in
 * the open array already carrying `status === 'closed'` (the state flipped
 * between the two fetches) is routed to the closed lane too.
 */
import type { BoardRow as BoardRowData } from '../../AgentConsole';

/** Re-exported so the tests (and the row component) name ONE row shape. */
export type { BoardRowData };

/**
 * The "just replied" window, in minutes. Same 15 the classic board sends to
 * the server as `replied_within_minutes` — long enough to still catch a lead
 * mid-exchange, short enough that everything in `active_now` is really live.
 * The classic copy is module-private in AgentConsole, so v2 declares its own
 * and the filter chip that flips the server param reads it from here.
 */
export const RECENT_REPLY_MINUTES = 15;
const RECENT_REPLY_MS = RECENT_REPLY_MINUTES * 60_000;

/** The server's cap on the closed fetch — the lane says so rather than lying
 *  about being "everything that ever closed". */
export const CLOSED_LANE_LIMIT = 25;

export type LaneKey =
  | 'needs_you'
  | 'active_now'
  | 'waiting_on_lead'
  | 'scheduled_idle'
  | 'recently_closed';

export interface Lane {
  key: LaneKey;
  label: string;
  rows: BoardRowData[];
  /** The lane is showing a server-capped window, not the whole truth. */
  capped?: boolean;
}

/** Lane order = triage order. The board renders them exactly like this. */
export const LANE_ORDER: LaneKey[] = [
  'needs_you',
  'active_now',
  'waiting_on_lead',
  'scheduled_idle',
  'recently_closed',
];

export const LANE_LABEL: Record<LaneKey, string> = {
  needs_you: 'Needs you',
  active_now: 'Active now',
  waiting_on_lead: 'Waiting on lead',
  scheduled_idle: 'Scheduled & idle',
  // The closed lane appends its window size — see closedLaneLabel().
  recently_closed: 'Recently closed',
};

/** "Recently closed (last 25)" — honest about the window, never silent. */
export const closedLaneLabel = (n: number) => `${LANE_LABEL.recently_closed} (last ${n})`;

// ---------------------------------------------------------------------------
// Time helpers. Every timestamp on a board row is a nullable ISO string that
// arrives from the API, so each one is parsed defensively: a malformed value
// must sort last, never NaN-poison a comparator into a random order.
// ---------------------------------------------------------------------------

const ts = (iso: string | null | undefined): number => {
  if (!iso) return Number.NaN;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.NaN : t;
};

/** When anything last happened on this row — the board's activity clock. */
export const lastActivityAt = (r: BoardRowData): number => {
  const m = ts(r.last_message?.occurred_at);
  if (!Number.isNaN(m)) return m;
  const c = ts(r.created_at);
  return Number.isNaN(c) ? 0 : c;
};

/** Final tiebreak so a lane's order depends on the ROWS, not on the order the
 *  server happened to return them in (and so the tests can assert exact ids). */
const byId = (a: BoardRowData, b: BoardRowData) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

// ---------------------------------------------------------------------------
// The predicates, one per lane, exported so the tests read like the spec.
// ---------------------------------------------------------------------------

/** 1. Somebody has to do something: a reply is held for review, the guardrails
 *  raised a flag, or a draft is sitting in the queue for this thread. */
export const isNeedsYou = (r: BoardRowData, draftConvoIds: Set<string>): boolean =>
  r.status === 'awaiting_review' || (r.open_flags || 0) > 0 || draftConvoIds.has(r.id);

/** 2. Moving right now: the agent is composing, or a message landed inside the
 *  reply window (either direction — an exchange in flight is an exchange). */
export const isActiveNow = (r: BoardRowData, nowTs: number): boolean => {
  if (r.drafting_since) return true;
  const m = ts(r.last_message?.occurred_at);
  return !Number.isNaN(m) && nowTs - m <= RECENT_REPLY_MS;
};

/** 3. We spoke last on a live thread — the ball is in the lead's court. */
export const isWaitingOnLead = (r: BoardRowData): boolean =>
  r.status === 'active' && !!r.last_message && r.last_message.direction !== 'inbound';

/**
 * The single first-match-wins decision. `scheduled_idle` is the floor: paused
 * threads, future callbacks, quiet actives where the lead spoke last and
 * nothing is owed, and rows with no message at all.
 */
export function laneKeyFor(r: BoardRowData, draftConvoIds: Set<string>, nowTs: number): LaneKey {
  if (r.status === 'closed') return 'recently_closed';
  if (isNeedsYou(r, draftConvoIds)) return 'needs_you';
  if (isActiveNow(r, nowTs)) return 'active_now';
  if (isWaitingOnLead(r)) return 'waiting_on_lead';
  return 'scheduled_idle';
}

// ---------------------------------------------------------------------------
// Sorting — each lane is ordered by the question it answers.
// ---------------------------------------------------------------------------

/** Triage debt: the thing that has waited longest is the thing to open. */
const byOldestActivity = (a: BoardRowData, b: BoardRowData) =>
  lastActivityAt(a) - lastActivityAt(b) || byId(a, b);

/** Everywhere else: newest movement on top, like any messaging surface. */
const byNewestActivity = (a: BoardRowData, b: BoardRowData) =>
  lastActivityAt(b) - lastActivityAt(a) || byId(a, b);

/** Active now: whoever came back to US most recently, then activity, then id.
 *  Rows the lead has never answered sort last — they are noise in this lane. */
const byNewestLeadReply = (a: BoardRowData, b: BoardRowData) => {
  const ra = ts(a.last_lead_reply_at);
  const rb = ts(b.last_lead_reply_at);
  const na = Number.isNaN(ra);
  const nb = Number.isNaN(rb);
  if (na !== nb) return na ? 1 : -1; // nulls last
  if (!na && ra !== rb) return rb - ra;
  return byNewestActivity(a, b);
};

const LANE_SORT: Record<LaneKey, (a: BoardRowData, b: BoardRowData) => number> = {
  needs_you: byOldestActivity,
  active_now: byNewestLeadReply,
  waiting_on_lead: byNewestActivity,
  scheduled_idle: byNewestActivity,
  recently_closed: byNewestActivity,
};

// ---------------------------------------------------------------------------
// The one entry point.
// ---------------------------------------------------------------------------

/**
 * Fold the two server fetches into the five triage lanes.
 *
 * @param open   the open-status board (active / paused / awaiting_review)
 * @param closed the closed-status board (server-capped at CLOSED_LANE_LIMIT)
 * @param draftConvoIds conversations with a draft waiting on a human
 * @param nowTs  the console's coarse clock (drives the reply window)
 */
export function groupBoardRows(
  open: BoardRowData[],
  closed: BoardRowData[],
  draftConvoIds: Set<string>,
  nowTs: number,
): Lane[] {
  const buckets: Record<LaneKey, BoardRowData[]> = {
    needs_you: [],
    active_now: [],
    waiting_on_lead: [],
    scheduled_idle: [],
    recently_closed: [],
  };

  // The closed fetch goes first so it owns any id both lists claim: when a
  // conversation closes between the two requests, the newer state wins and the
  // row is never rendered twice.
  const seen = new Set<string>();
  for (const r of closed || []) {
    if (!r || seen.has(r.id)) continue;
    seen.add(r.id);
    buckets.recently_closed.push(r);
  }
  for (const r of open || []) {
    if (!r || seen.has(r.id)) continue;
    seen.add(r.id);
    buckets[laneKeyFor(r, draftConvoIds, nowTs)].push(r);
  }

  return LANE_ORDER.map((key) => {
    const rows = buckets[key].slice().sort(LANE_SORT[key]);
    if (key !== 'recently_closed') return { key, label: LANE_LABEL[key], rows };
    return {
      key,
      label: closedLaneLabel(rows.length),
      rows,
      capped: rows.length >= CLOSED_LANE_LIMIT,
    };
  });
}

// ---------------------------------------------------------------------------
// Client-side search — the one filter that does NOT go to the server.
// ---------------------------------------------------------------------------

/**
 * Does this row match the board's search box? Name, study and the last-message
 * snippet, case-insensitively, every whitespace-separated token having to hit
 * somewhere ("jane bp" finds Jane on BP-303). An empty query matches
 * everything, so callers can filter unconditionally.
 */
export function matchesBoardQuery(r: BoardRowData, query: string): boolean {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = [r.contact_name, r.study_name, r.last_message?.body]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((token) => hay.includes(token));
}
